import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Droplets, Shield, AlertTriangle } from 'lucide-react';
import { useTokenPrice } from '../../hooks/useTokenPrice';
import { useOnChainStats } from '../../hooks/useOnChainStats';
import { POPULAR_MONAD_TOKENS } from '../../lib/WebAgentEngine';

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    green: '#22c55e',
    red: '#ef4444',
    orange: '#FF8C00',
    blue: '#3b82f6',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(181, 111, 111, 0.2)',
};

const cardStyle: React.CSSProperties = {
    background: THEME.card,
    border: `1px solid ${THEME.border}`,
    borderRadius: '20px',
    overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
};

const bodyStyle: React.CSSProperties = {
    padding: '1.5rem',
};

function getHealthBar(value: number): React.ReactNode {
    const filled = Math.round(value / 10);
    const color = value >= 70 ? THEME.green : value >= 40 ? THEME.yellow : THEME.red;
    return (
        <div style={{ display: 'flex', gap: '2px' }}>
            {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} style={{
                    width: '12px', height: '12px', borderRadius: '3px',
                    background: i < filled ? color : 'rgba(255,255,255,0.05)',
                }} />
            ))}
        </div>
    );
}

const LiquidityMonitor: React.FC<{ tokenAddress?: string }> = ({ tokenAddress }) => {
    const tokenConfig = tokenAddress
        ? POPULAR_MONAD_TOKENS.find(t => t.address.toLowerCase() === tokenAddress.toLowerCase()) || { address: tokenAddress as `0x${string}`, symbol: 'TOKEN', name: 'Token' }
        : POPULAR_MONAD_TOKENS.find(t => t.symbol === 'DUCK') || POPULAR_MONAD_TOKENS[0];
    const price = useTokenPrice(tokenConfig);
    const stats = useOnChainStats(tokenAddress, 900_000);

    const bondingProgress = price.bondingProgress || 0;
    const isGraduated = price.isGraduated || false;
    const liquidity = stats?.liquidity || 0;
    const volume24h = stats?.volume24h || 0;
    const buys = stats?.buys24h || 0;
    const sells = stats?.sells24h || 0;

    // Calculate rug risk (simplified version of backend logic)
    const rugRisk = React.useMemo(() => {
        let risk = 0;
        if (liquidity < 1000) risk += 35;
        else if (liquidity < 5000) risk += 20;
        else if (liquidity < 10000) risk += 10;
        if (!isGraduated) risk += 15;
        if (sells > 0 && buys > 0) {
            const ratio = sells / buys;
            if (ratio > 3) risk += 25;
            else if (ratio > 2) risk += 15;
            else if (ratio > 1.5) risk += 5;
        }
        if (volume24h < 100) risk += 10;
        return Math.min(100, Math.max(0, risk));
    }, [liquidity, isGraduated, buys, sells, volume24h]);

    const lpHealth = liquidity >= 10000 ? 'HEALTHY' : liquidity >= 5000 ? 'STABLE' : liquidity >= 1000 ? 'LOW' : 'CRITICAL';
    const lpColor = lpHealth === 'HEALTHY' ? THEME.green : lpHealth === 'STABLE' ? THEME.yellow : lpHealth === 'LOW' ? THEME.orange : THEME.red;

    // LP metrics chart data
    const metricsData = [
        { name: 'Liquidity', value: Math.min(liquidity / 100, 100), color: THEME.blue },
        { name: 'Volume', value: Math.min(volume24h / 100, 100), color: THEME.purple },
        { name: 'Safety', value: 100 - rugRisk, color: rugRisk < 30 ? THEME.green : rugRisk < 60 ? THEME.yellow : THEME.red },
        { name: 'Progress', value: isGraduated ? 100 : bondingProgress, color: isGraduated ? THEME.green : THEME.yellow },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Droplets size={24} color={THEME.blue} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>Liquidity Monitor</h3>
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                <div style={{ background: `${THEME.blue}10`, border: `1px solid ${THEME.blue}25`, borderRadius: '16px', padding: '1.25rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: THEME.blue }}>${(liquidity / 1000).toFixed(1)}K</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase' }}>Liquidity</div>
                </div>
                <div style={{ background: `${lpColor}10`, border: `1px solid ${lpColor}25`, borderRadius: '16px', padding: '1.25rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: lpColor }}>{lpHealth}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase' }}>LP Health</div>
                </div>
                <div style={{ background: `${isGraduated ? THEME.green : THEME.yellow}10`, border: `1px solid ${isGraduated ? THEME.green : THEME.yellow}25`, borderRadius: '16px', padding: '1.25rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: isGraduated ? THEME.green : THEME.yellow }}>
                        {isGraduated ? 'YES' : `${bondingProgress.toFixed(0)}%`}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase' }}>Graduated</div>
                </div>
                <div style={{ background: `${rugRisk < 40 ? THEME.green : rugRisk < 70 ? THEME.orange : THEME.red}10`, border: `1px solid ${rugRisk < 40 ? THEME.green : rugRisk < 70 ? THEME.orange : THEME.red}25`, borderRadius: '16px', padding: '1.25rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: rugRisk < 40 ? THEME.green : rugRisk < 70 ? THEME.orange : THEME.red }}>{rugRisk}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem', textTransform: 'uppercase' }}>Rug Risk</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Bonding Curve Progress */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <Shield size={18} color={THEME.yellow} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Bonding Curve</h3>
                    </div>
                    <div style={{ ...bodyStyle, textAlign: 'center' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{
                                width: '100%', height: '24px', background: 'rgba(0,0,0,0.4)',
                                borderRadius: '12px', overflow: 'hidden', position: 'relative',
                            }}>
                                <div style={{
                                    width: `${isGraduated ? 100 : bondingProgress}%`, height: '100%',
                                    background: isGraduated
                                        ? `linear-gradient(90deg, ${THEME.green}, ${THEME.yellow})`
                                        : `linear-gradient(90deg, ${THEME.purple}, ${THEME.yellow})`,
                                    borderRadius: '12px', transition: 'width 1s ease',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }} />
                            </div>
                            <div style={{ marginTop: '0.5rem', fontSize: '1.5rem', fontWeight: 800, color: isGraduated ? THEME.green : THEME.yellow }}>
                                {isGraduated ? 'GRADUATED' : `${bondingProgress.toFixed(1)}%`}
                            </div>
                        </div>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {isGraduated
                                ? 'Token has graduated from bonding curve to DEX liquidity pool.'
                                : `${(100 - bondingProgress).toFixed(1)}% remaining until graduation to DEX.`
                            }
                        </p>
                    </div>
                </div>

                {/* LP Metrics */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <Droplets size={18} color={THEME.blue} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>LP Metrics</h3>
                    </div>
                    <div style={bodyStyle}>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={metricsData} layout="vertical">
                                <XAxis type="number" domain={[0, 100]} stroke="#6b7280" fontSize={12} />
                                <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={12} width={70} />
                                <Tooltip
                                    contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                    labelStyle={{ color: '#fff' }}
                                />
                                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                    {metricsData.map((entry, i) => (
                                        <Cell key={i} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Risk Assessment */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <AlertTriangle size={18} color={rugRisk < 40 ? THEME.green : THEME.orange} />
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase' }}>Risk Assessment</h3>
                </div>
                <div style={bodyStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem', minWidth: '80px' }}>Rug Risk:</span>
                        {getHealthBar(100 - rugRisk)}
                        <span style={{ color: rugRisk < 40 ? THEME.green : rugRisk < 70 ? THEME.orange : THEME.red, fontWeight: 700 }}>{rugRisk}/100</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {[
                            { label: 'Liquidity', ok: liquidity >= 5000, detail: `$${(liquidity / 1000).toFixed(1)}K` },
                            { label: 'Graduation', ok: isGraduated, detail: isGraduated ? 'Graduated' : `${bondingProgress.toFixed(0)}% progress` },
                            { label: 'Buy/Sell Ratio', ok: buys >= sells, detail: `${buys}/${sells}` },
                            { label: 'Volume', ok: volume24h >= 1000, detail: `$${(volume24h / 1000).toFixed(1)}K 24h` },
                        ].map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.ok ? THEME.green : THEME.red }} />
                                <span style={{ color: '#d1d5db', fontSize: '0.875rem', flex: 1 }}>{item.label}</span>
                                <span style={{ color: item.ok ? THEME.green : THEME.red, fontSize: '0.875rem', fontWeight: 600 }}>{item.detail}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiquidityMonitor;
